import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LogOut, User, Settings, Bookmark, FlaskConical, DoorOpen } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import { api } from "@/lib/api";

// Shared account menu, used by the desktop header (top-right) and the mobile
// bottom nav (bottom-left). Now also the ONLY place Connie's chat is opened from.
export default function ProfileMenu({ trigger, side = "bottom", align = "end" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const isStaff = user.role === "owner" || user.role === "admin" || user.role === "brand_ambassador";
  const isMember = !isStaff;
  const openConnie = () => window.dispatchEvent(new Event("connie-open"));
  const viewAs = async (p) => {
    try { await api.post("/admin/test-persona", { persona: p }); window.location.href = p === "admin" ? "/admin" : "/"; } catch { /* noop */ }
  };
  const currentPersona = user.impersonating ? user.role : "admin";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="w-60 bg-slate-900/95 backdrop-blur-xl border-white/10 text-slate-100" data-testid="profile-menu-content">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-white truncate">{user.name || "Account"}</span>
          <span className="text-xs font-normal text-slate-400 capitalize">{user.role === "brand_ambassador" ? "Brand Ambassador" : user.role}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem data-testid="menu-connie-chat" onClick={openConnie} className="cursor-pointer focus:bg-white/10 focus:text-white">
          <img src="/connie-mark.png" alt="" className="w-4 h-4 mr-2 object-contain" /> Chat with Connie
        </DropdownMenuItem>
        {isMember && (
          <DropdownMenuItem data-testid="header-my-profile" onClick={() => navigate("/create-profile")} className="cursor-pointer focus:bg-white/10 focus:text-white">
            <User className="w-4 h-4 mr-2" /> {user.role === "business" ? "Business profile" : "My profile"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid="header-settings" onClick={() => navigate("/settings")} className="cursor-pointer focus:bg-white/10 focus:text-white">
          <Settings className="w-4 h-4 mr-2" /> Settings
        </DropdownMenuItem>
        {user.role === "salesman" && (
          <DropdownMenuItem data-testid="menu-closing-process" onClick={() => navigate("/closing-process")} className="cursor-pointer focus:bg-white/10 focus:text-white">
            <DoorOpen className="w-4 h-4 mr-2" /> The Closing Process
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid="menu-connie-connection" onClick={() => navigate("/connie-connection")} className="cursor-pointer focus:bg-white/10 focus:text-white">
          <Bookmark className="w-4 h-4 mr-2" /> The Connie Connection
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        {user.real_role === "owner" && (
          <>
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-amber-300/80 flex items-center gap-1.5 py-1 font-bold">
              <FlaskConical className="w-3.5 h-3.5" /> View as
            </DropdownMenuLabel>
            {[["admin", "Admin"], ["business", "Business"], ["salesman", "Sales"]].map(([p, label]) => (
              <DropdownMenuItem key={p} data-testid={`menu-view-as-${p}`} onClick={() => viewAs(p)} className="cursor-pointer focus:bg-white/10 focus:text-white">
                <span className={`w-2 h-2 rounded-full mr-2 ${currentPersona === p ? "bg-amber-400" : "bg-slate-600"}`} /> {label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-white/10" />
          </>
        )}
        <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid="menu-theme-toggle" className="p-0 focus:bg-transparent cursor-default">
          <ThemeToggle />
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem data-testid="header-logout" onClick={logout} className="cursor-pointer text-rose-300 focus:bg-rose-500/15 focus:text-rose-200">
          <LogOut className="w-4 h-4 mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
